/*
 * @Author: Ryan
 * @Date: 2026-01-08
 * @Description: 上传区域组件（复用基础 Upload 组件）
 */
import { DeleteOutlined, FileOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, message, Progress } from 'antd';
import { useRouter } from 'next/router';
import React, { useContext, useRef, useState } from 'react';

import Upload from '@/components/BatchUpload/common/upload';
import { getVideoInfo } from '@/components/BatchUpload/common/utils';
import { FileUploadStatus } from '@/components/BatchUpload/type';
import { RightContext } from '@/components/RightProvider/context';
import { useTranslate } from '@/hooks/useTranslate';
import { VIDEO_TRANSLATOR_URL } from '@/landing/video-translator/config';
import useSubtitleTranslationStore from '@/store/SubtitleTranslation';
import { FileUploader } from '@/utils/uploader';

import styles from './UploadArea.module.scss';

interface UploadAreaProps {
  ctaText: string;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  file: File;
  url: string;
  previewUrl: string;
  duration: number;
  resolution: string;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  errorMsg?: string;
}

const ACCEPT_VIDEO_TYPES = '.mp4,.mov,video/mp4,video/quicktime';
const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

// 格式化文件大小
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
};

const UploadArea: React.FC<UploadAreaProps> = ({ ctaText }) => {
  const { t } = useTranslate();
  const router = useRouter();
  const rightContext = useContext(RightContext);
  const uploaderRef = useRef<FileUploader | null>(null);

  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const setVideoList = useSubtitleTranslationStore((state) => state.setVideoList);

  // 验证文件
  const validateFile = (file: File): boolean => {
    const isValidType =
      file.type === 'video/mp4' ||
      file.type === 'video/quicktime' ||
      file.name.toLowerCase().endsWith('.mov');

    if (!isValidType) {
      message.error(t('merror.tip.fileFormats') + ': MP4, MOV');
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      message.error(t('sizeGB', { size: 2 }));
      return false;
    }

    if (file.size === 0) {
      message.error(t('merror.tip.zeroSizeFile'));
      return false;
    }

    return true;
  };

  // 上传文件
  const uploadFile = async (file: File) => {
    const fileId = `file_${Date.now()}`;

    // 初始化上传状态
    setUploadedFile({
      id: fileId,
      name: file.name,
      size: file.size,
      file,
      url: '',
      previewUrl: '',
      duration: 0,
      resolution: '',
      progress: 0,
      status: 'uploading',
    });

    try {
      // 获取视频信息
      const { width, height, duration } = await getVideoInfo(file);

      setUploadedFile((prev) =>
        prev
          ? {
              ...prev,
              duration: Number(duration),
              resolution: `${width}*${height}`,
            }
          : null,
      );

      // 创建上传器
      if (!uploaderRef.current) {
        uploaderRef.current = new FileUploader({
          onProgress: (data: { progress: number; status: boolean; url: string }) => {
            setUploadedFile((prev) =>
              prev ? { ...prev, progress: data.progress } : null,
            );
          },
        });
      }

      // 上传文件
      const url = await uploaderRef.current.upload({
        file,
        tokenData: {
          fileName: file.name,
          dirSceneType: 20,
        },
      });

      // 上传成功
      setUploadedFile((prev) =>
        prev
          ? {
              ...prev,
              url,
              progress: 100,
              status: 'success',
            }
          : null,
      );

      // 存储到 Store
      setVideoList([
        {
          id: fileId,
          name: file.name.replace(/\.[^.]+$/, ''),
          file,
          url,
          previewUrl: '',
          duration: Number(duration),
          resolution: `${width}*${height}`,
          progress: 100,
          status: FileUploadStatus.Success,
          from: 'landing',
        },
      ]);
    } catch (error: any) {
      console.error('Upload failed:', error);
      setUploadedFile((prev) =>
        prev
          ? {
              ...prev,
              status: 'error',
              errorMsg: error?.message || t('merror.tip.uploadFailed'),
            }
          : null,
      );
      message.error(t('merror.tip.uploadFailed'));
    }
  };

  // 处理文件选择
  const handleFileChange = (files: File[]) => {
    if (files.length === 0) return;

    const file = files[0];
    if (validateFile(file)) {
      uploadFile(file);
    }
  };

  // 处理拖拽
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(
      (file) =>
        file.type === 'video/mp4' ||
        file.type === 'video/quicktime' ||
        file.name.toLowerCase().endsWith('.mov'),
    );

    if (files.length > 0) {
      handleFileChange(files);
    } else if (e.dataTransfer.files.length > 0) {
      message.error(t('merror.tip.fileFormats') + ': MP4, MOV');
    }
  };

  // 删除文件
  const handleRemoveFile = () => {
    uploaderRef.current?.cancel?.();
    setUploadedFile(null);
    setVideoList([]);
  };

  // 开始处理
  const handleStartProcessing = async () => {
    if (!uploadedFile || uploadedFile.status !== 'success') return;

    // 检查登录状态
    if (!rightContext.getIsLoggedIn()) {
      await rightContext.getUserLoginState({
        onReady: () => {
          router.push(VIDEO_TRANSLATOR_URL);
        },
      });
      return;
    }

    router.push(VIDEO_TRANSLATOR_URL);
  };

  // 已上传文件状态
  if (uploadedFile) {
    return (
      <div className={styles.uploadArea}>
        <div className={styles.uploadedContainer}>
          {/* 文件信息 */}
          <div className={styles.fileInfo}>
            <FileOutlined className={styles.fileIcon} />
            <span className={styles.fileName}>{uploadedFile.name}</span>
            <span className={styles.fileSize}>{formatFileSize(uploadedFile.size)}</span>
            <button
              type="button"
              className={styles.deleteButton}
              onClick={handleRemoveFile}
              aria-label="Remove file"
            >
              <DeleteOutlined />
            </button>
          </div>

          {/* 上传进度 */}
          {uploadedFile.status === 'uploading' && (
            <Progress
              percent={Math.round(uploadedFile.progress)}
              size="small"
              className={styles.progressBar}
            />
          )}

          {/* 上传成功后显示按钮 */}
          {uploadedFile.status === 'success' && (
            <>
              <Button
                type="primary"
                className={styles.processButton}
                onClick={handleStartProcessing}
              >
                Start Processing
              </Button>
              <Upload
                accept={ACCEPT_VIDEO_TYPES}
                onFileChange={handleFileChange}
                onDrop={handleDrop}
                requireLogin={false}
              >
                <span className={styles.selectAnotherLink}>
                  or, <a>select another file</a>
                </span>
              </Upload>
            </>
          )}

          {/* 上传失败 */}
          {uploadedFile.status === 'error' && (
            <Upload
              accept={ACCEPT_VIDEO_TYPES}
              onFileChange={handleFileChange}
              onDrop={handleDrop}
              requireLogin={false}
            >
              <Button type="primary" className={styles.retryButton}>
                {t('merror.operate.retry')}
              </Button>
            </Upload>
          )}
        </div>
      </div>
    );
  }

  // 默认状态（未选择文件）
  return (
    <Upload
      accept={ACCEPT_VIDEO_TYPES}
      onFileChange={handleFileChange}
      onDrop={handleDrop}
      className={styles.uploadWrapper}
    >
      <div
        className={`${styles.uploadArea} ${styles.defaultState} ${isDragging ? styles.dragging : ''}`}
        onDragEnter={() => setIsDragging(true)}
        onDragLeave={() => setIsDragging(false)}
      >
        <p className={styles.dragText}>Drag& Drop, or</p>
        <Button type="primary" icon={<UploadOutlined />} className={styles.uploadButton}>
          {ctaText}
        </Button>
      </div>
    </Upload>
  );
};

export default UploadArea;
